import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';

import { AuthProvider } from '@/auth/auth-context';
import { ToastProvider } from '@/components/ui/Toast';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { i18n } from '@/i18n';
import { ThemeProvider } from '@/theme/theme-context';

/**
 * Provider order is deliberate: Query first, because AuthProvider clears the cache on sign-out;
 * i18n and Theme next, because everything below renders text and colour; Tooltip and Toast last,
 * since they only wrap overlay behaviour. There is ONE toast host for the whole application —
 * a second stack in a subtree would mean two places a receipt could appear.
 */
export function Providers({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: ReactNode;
}): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <AuthProvider>
            <TooltipProvider delayDuration={200} skipDelayDuration={300}>
              <ToastProvider>{children}</ToastProvider>
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}
